// The code overlay. Styled after Hydra: no panel, each line's text sits on its own dark box over
// the running game, and evaluated code flashes. The editor is also the REPL: Alt+Enter evaluates
// the form under the cursor.
import { autocompletion, type CompletionContext, type CompletionResult } from '@codemirror/autocomplete';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import {
  bracketMatching,
  ensureSyntaxTree,
  HighlightStyle,
  indentOnInput,
  syntaxHighlighting,
  syntaxTree,
} from '@codemirror/language';
import { type Diagnostic, forceLinting, linter } from '@codemirror/lint';
import { EditorState, Prec, StateEffect, StateField, type Text } from '@codemirror/state';
import { Decoration, type DecorationSet, drawSelection, EditorView, keymap } from '@codemirror/view';
import { tags as t } from '@lezer/highlight';
import { clojure } from '@nextjournal/lang-clojure';
import { closestKind, isKnownKind, kinds, laserKinds } from './kinds.ts';
import { builtins, specialForms } from './language.ts';
import type { SourceError } from './sim.ts';

// Tomorrow Night Eighties, as Hydra uses it.
const highlight = HighlightStyle.define([
  { tag: t.keyword, color: '#f2777a' },
  { tag: t.definition(t.variableName), color: '#f99157' },
  { tag: t.variableName, color: '#99cc99' },
  { tag: [t.atom, t.number, t.bool, t.null], color: '#a16a94' },
  { tag: [t.lineComment, t.comment], color: '#d27b53' },
  { tag: t.string, color: '#ffcc66' },
]);

const theme = EditorView.theme(
  {
    '&': { color: '#cccccc', backgroundColor: 'transparent' },
    '&.cm-focused': { outline: 'none' },
    '.cm-scroller': { fontFamily: 'monospace', lineHeight: '1.4' },
    '.cm-content': { padding: '0', caretColor: '#999' },
    '.cm-line': {
      width: 'fit-content',
      padding: '0 4px',
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
    },
    // Empty lines get no box, as in Hydra.
    '.cm-line:has(> br:only-child)': { backgroundColor: 'transparent' },
    '.cm-cursor, .cm-dropCursor': { borderLeft: '3px solid #999' },
    '.cm-selectionBackground, &.cm-focused .cm-selectionBackground': {
      backgroundColor: 'rgba(130, 130, 130, 0.9) !important',
    },
    '.cm-matchingBracket, &.cm-focused .cm-matchingBracket': {
      backgroundColor: 'transparent',
      color: '#ffffff',
      textDecoration: 'underline',
    },
    '.cm-flash': {
      background: 'rgba(0, 255, 255, 0.5)',
      mixBlendMode: 'difference',
    },
    '.cm-lintRange-error': {
      backgroundImage: 'none',
      textDecoration: 'underline wavy #f2777a',
      textUnderlineOffset: '4px',
    },
    '.cm-lintRange-warning': {
      backgroundImage: 'none',
      textDecoration: 'underline wavy #ffcc66',
      textUnderlineOffset: '4px',
    },
    '.cm-tooltip': {
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      border: 'none',
      color: '#cccccc',
      fontFamily: 'monospace',
      fontSize: '14px',
    },
    '.cm-diagnostic-error': { borderLeft: '3px solid #f2777a' },
    '.cm-diagnostic-warning': { borderLeft: '3px solid #ffcc66' },
    '.cm-diagnosticAction': {
      backgroundColor: '#cccccc',
      color: '#000',
      borderRadius: '0',
      font: 'inherit',
    },
    '.cm-tooltip.cm-tooltip-autocomplete > ul': { fontFamily: 'monospace', maxHeight: '14em' },
    '.cm-tooltip.cm-tooltip-autocomplete > ul > li': { padding: '0 6px' },
    '.cm-tooltip.cm-tooltip-autocomplete > ul > li[aria-selected]': {
      backgroundColor: '#cccccc',
      color: '#000',
    },
    '.cm-completionIcon': { display: 'none' },
    '.cm-completionDetail': { color: '#999', fontStyle: 'normal' },
    '.cm-completionMatchedText': { textDecoration: 'none', color: '#f99157' },
    '.cm-tooltip.cm-completionInfo': { padding: '2px 8px', maxWidth: '28em' },
  },
  { dark: true },
);

// Evaluated code flashes for a moment.
const flashEffect = StateEffect.define<{ from: number; to: number } | null>();
const flashMark = Decoration.mark({ class: 'cm-flash' });
const flashField = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update(marks, tr) {
    marks = marks.map(tr.changes);
    for (const e of tr.effects) {
      if (!e.is(flashEffect)) continue;
      const r = e.value;
      marks = r && r.to > r.from ? Decoration.set([flashMark.range(r.from, r.to)]) : Decoration.none;
    }
    return marks;
  },
  provide: (f) => EditorView.decorations.from(f),
});

export function flash(view: EditorView, from: number, to: number): void {
  view.dispatch({ effects: flashEffect.of({ from, to }) });
  setTimeout(() => view.dispatch({ effects: flashEffect.of(null) }), 300);
}

/** A piece of the document to evaluate, and where it starts so errors can point back into it. */
export interface Block {
  text: string;
  from: number;
  to: number;
  line: number;
  col: number;
}

/** The selection if there is one, otherwise the top-level form under (or on the line of) the cursor. */
export function blockAt(state: EditorState): Block | null {
  let { from, to } = state.selection.main;
  if (from === to) {
    const tree = ensureSyntaxTree(state, state.doc.length, 200);
    const pos = state.selection.main.head;
    const line = state.doc.lineAt(pos);
    let found: { from: number; to: number } | null = null;
    for (let node = tree?.topNode.firstChild; node; node = node.nextSibling) {
      if (node.name === 'LineComment' || node.name === 'Discard') continue;
      if (node.from <= pos && pos <= node.to) {
        found = node;
        break;
      }
      // The cursor is on the same line as a form but outside it, e.g. just past the last paren.
      if (!found && node.to >= line.from && node.from <= line.to) found = node;
    }
    if (!found) return null;
    ({ from, to } = found);
  }
  const start = state.doc.lineAt(from);
  return { text: state.sliceDoc(from, to), from, to, line: start.number, col: from - start.from + 1 };
}

// Lint: `:kind 'name` literals that kinds.ts doesn't know, plus the last load's errors.

const KIND_LITERAL = /:kind\s+'([^\s()[\]{}";]+)/g;

function kindWarnings(state: EditorState): Diagnostic[] {
  const tree = syntaxTree(state);
  const out: Diagnostic[] = [];
  for (const m of state.doc.toString().matchAll(KIND_LITERAL)) {
    const name = m[1];
    if (isKnownKind(name) || tree.resolveInner(m.index, 1).name.includes('Comment')) continue;
    const to = m.index + m[0].length;
    const from = to - name.length - 1;
    const near = closestKind(name);
    out.push({
      from,
      to,
      severity: 'warning',
      message: near ? `unknown kind '${name}; did you mean '${near}?` : `unknown kind '${name}; it draws as bullet`,
      actions: near
        ? [
            {
              name: `use '${near}`,
              apply: (view, f, t) => view.dispatch({ changes: { from: f, to: t, insert: `'${near}` } }),
            },
          ]
        : [],
    });
  }
  return out;
}

/** Load errors stay marked until the text changes. */
let loadErrors: { doc: Text; diagnostics: Diagnostic[] } | null = null;
const relint = StateEffect.define<null>();

const lint = linter(
  (view) => [
    ...kindWarnings(view.state),
    ...(loadErrors && loadErrors.doc === view.state.doc ? loadErrors.diagnostics : []),
  ],
  {
    delay: 250,
    needsRefresh: (update) => update.transactions.some((tr) => tr.effects.some((e) => e.is(relint))),
  },
);

// Completion: kind names after a quote, special forms and builtins for plain words.

const kindOptions = [
  ...Object.keys(kinds).map((label) => ({ label, type: 'constant', detail: 'kind' })),
  ...Object.keys(laserKinds).map((label) => ({ label, type: 'constant', detail: 'laser kind' })),
];
const wordOptions = [
  ...specialForms.map((w) => ({ label: w.name, type: 'keyword', info: w.info })),
  ...builtins.filter((w) => /^[a-z]/.test(w.name)).map((w) => ({ label: w.name, type: 'function', info: w.info })),
];

const builtinKeys = new Map(builtins.flatMap((w) => (w.keys ? [[w.name, w.keys] as const] : [])));

/** The innermost call around `pos`: its head and its text up to `pos`. Null inside a vector. */
function enclosingCall(doc: string, pos: number): { head: string; text: string } | null {
  let depth = 0;
  for (let i = pos - 1; i >= 0; i--) {
    const c = doc[i];
    if (c === ')' || c === ']') {
      depth++;
    } else if (c === '(' || c === '[') {
      if (depth > 0) {
        depth--;
        continue;
      }
      const head = c === '(' ? /^\(([a-z][a-z0-9?-]*)/.exec(doc.slice(i, i + 64)) : null;
      return head ? { head: head[1], text: doc.slice(i, pos) } : null;
    }
  }
  return null;
}

/** A form's top-level items, nested forms kept whole: "n :dir (aim) :kind 'b" → 5 items. */
function items(text: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let item = '';
  for (const c of text) {
    if (depth === 0 && /\s/.test(c)) {
      if (item) out.push(item);
      item = '';
      continue;
    }
    if (c === '(' || c === '[') depth++;
    if (c === ')' || c === ']') depth--;
    item += c;
  }
  if (item) out.push(item);
  return out;
}

/** The keyword parameters of every top-level defn in the editor, with their defaults. */
function userKeys(doc: string): Map<string, [string, string][]> {
  const found = new Map<string, [string, string][]>();
  for (const m of doc.matchAll(/\(defn\s+([a-z][a-z0-9?-]*)\s*\[/g)) {
    let i = m.index + m[0].length;
    let depth = 1;
    const start = i;
    while (i < doc.length && depth > 0) {
      if (doc[i] === '[' || doc[i] === '(') depth++;
      if (doc[i] === ']' || doc[i] === ')') depth--;
      i++;
    }
    const params = items(doc.slice(start, i - 1));
    const first = params.findIndex((p) => p.startsWith(':'));
    if (first < 0) continue;

    const keys: [string, string][] = [];
    for (let k = first; k < params.length; k += 2) keys.push([params[k].slice(1), params[k + 1] ?? '']);
    found.set(m[1], keys);
  }
  return found;
}

/** :keys for the call around the cursor, while typing a key or just after the head. */
function completeKeys(ctx: CompletionContext): CompletionResult | null {
  const key = ctx.matchBefore(/:[a-z0-9-]*/);
  const doc = ctx.state.doc.toString();
  const call = enclosingCall(doc, key ? key.from : ctx.pos);
  if (!call) return null;

  // Without a key typed yet, only offer them straight after `(name `.
  const justHead = call.text.startsWith(`(${call.head}`) && /^\s+$/.test(call.text.slice(call.head.length + 1));
  if (!key && !(ctx.explicit || justHead)) return null;

  const keys = userKeys(doc).get(call.head) ?? builtinKeys.get(call.head);
  if (!keys) return null;

  const given = new Set(items(call.text.slice(1)).filter((t) => t.startsWith(':')));
  const options = keys
    .filter(([name]) => !given.has(`:${name}`))
    .map(([name, fallback]) => ({ label: `:${name}`, type: 'property', detail: fallback }));
  if (options.length === 0) return null;

  return { from: key ? key.from : ctx.pos, options, validFor: /^:?[a-z0-9-]*$/ };
}

function complete(ctx: CompletionContext): CompletionResult | null {
  if (syntaxTree(ctx.state).resolveInner(ctx.pos, -1).name.includes('Comment')) return null;
  const quoted = ctx.matchBefore(/'[a-z0-9-]*/);
  if (quoted) return { from: quoted.from + 1, options: kindOptions, validFor: /^[a-z0-9-]*$/ };

  const keys = completeKeys(ctx);
  if (keys || ctx.matchBefore(/:[a-z0-9-]*/)) return keys;

  const word = ctx.matchBefore(/[a-z][a-z0-9?-]*/);
  if (!word || (word.to - word.from < 2 && !ctx.explicit)) return null;
  return { from: word.from, options: wordOptions, validFor: /^[a-z0-9?-]*$/ };
}

export interface EditorKeys {
  /** Ctrl/Cmd+Enter: load the whole file. */
  runAll: () => void;
  /** Alt+Enter: evaluate the form under the cursor, or the selection. */
  runBlock: () => void;
  /** Ctrl/Cmd+Shift+Enter: start over with a fresh VM. */
  reset: () => void;
  /** Alt+P, Alt+period, Alt+comma: pause, step forward, step back. */
  pause: () => void;
  step: () => void;
  back: () => void;
  /** Ctrl/Cmd+S: copy a share link. With Shift, the ASCII form. */
  share: (ascii: boolean) => void;
  /** Shift+Alt+F: format the code. */
  format: () => void;
  onChange: () => void;
}

export function createEditor(parent: HTMLElement, keys: EditorKeys): EditorView {
  const bind = (key: string, run: () => void) => ({
    key,
    run: () => {
      run();
      return true;
    },
  });
  return new EditorView({
    parent,
    state: EditorState.create({
      extensions: [
        Prec.highest(
          keymap.of([
            bind('Mod-Shift-Enter', keys.reset),
            bind('Mod-Enter', keys.runAll),
            bind('Alt-Enter', keys.runBlock),
            bind('Alt-p', keys.pause),
            bind('Alt-.', keys.step),
            bind('Alt-,', keys.back),
            bind('Mod-s', () => keys.share(false)),
            bind('Mod-Shift-s', () => keys.share(true)),
            bind('Shift-Alt-f', keys.format),
          ]),
        ),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) keys.onChange();
        }),
        history(),
        drawSelection(),
        indentOnInput(),
        bracketMatching(),
        clojure(),
        syntaxHighlighting(highlight),
        flashField,
        lint,
        autocompletion({ override: [complete], icons: false }),
        theme,
        keymap.of([...defaultKeymap, ...historyKeymap, indentWithTab]),
      ],
    }),
  });
}

export function setText(view: EditorView, text: string): void {
  view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: text } });
}

/**
 * Replaces the whole text as one undoable change, keeping the cursor next to the same code: it
 * counts the characters before the cursor that aren't spaces and finds that count again in the
 * new text. Returns whether anything changed.
 */
export function replaceKeepingCursor(view: EditorView, text: string): boolean {
  const old = view.state.doc.toString();
  if (old === text) return false;

  const head = view.state.selection.main.head;
  let solid = 0;
  for (let i = 0; i < head; i++) if (!/\s/.test(old[i])) solid++;

  let at = 0;
  while (at < text.length && solid > 0) {
    if (!/\s/.test(text[at])) solid--;
    at++;
  }

  view.dispatch({
    changes: { from: 0, to: old.length, insert: text },
    selection: { anchor: at },
    scrollIntoView: true,
  });
  return true;
}

/**
 * Inserts a symbol at the cursor, replacing any selection, with a space either side where it
 * would otherwise run into its neighbours. Then puts focus back in the editor.
 */
export function insertSymbol(view: EditorView, text: string): void {
  const { from, to } = view.state.selection.main;
  const before = from > 0 ? view.state.sliceDoc(from - 1, from) : '';
  const after = view.state.sliceDoc(to, to + 1);
  const lead = before === '' || /[\s([{]/.test(before) ? '' : ' ';
  const trail = after === '' || /[\s)\]}]/.test(after) ? '' : ' ';
  const insert = lead + text + trail;
  view.dispatch({
    changes: { from, to, insert },
    selection: { anchor: from + lead.length + text.length },
    scrollIntoView: true,
  });
  view.focus();
}

/**
 * Marks errors (red) and warnings (yellow) in the editor. Line and column start at 1; line 0
 * means no position.
 */
export function showErrors(view: EditorView, errors: SourceError[], warnings: SourceError[] = []): void {
  const doc = view.state.doc;
  const marked: [SourceError, 'error' | 'warning'][] = [
    ...errors.map((e): [SourceError, 'error'] => [e, 'error']),
    ...warnings.map((e): [SourceError, 'warning'] => [e, 'warning']),
  ];
  const diagnostics: Diagnostic[] = marked.map(([e, severity]) => {
    if (e.line < 1 || e.line > doc.lines) {
      return { from: 0, to: Math.min(1, doc.length), severity, message: e.message };
    }
    const line = doc.line(e.line);
    const from = Math.min(line.from + Math.max(0, e.col - 1), line.to);
    // Underline the token there, or the call's name when the position is its opening paren.
    const word = /^[([]?[^\s()[\]]*/.exec(doc.sliceString(from, line.to));
    const to = Math.min(from + Math.max(1, word ? word[0].length : 1), doc.length);
    return { from, to, severity, message: e.message };
  });
  loadErrors = { doc, diagnostics };
  view.dispatch({ effects: relint.of(null) });
  forceLinting(view);
}
